import { useState } from 'react';
import { Activity, FileText, KeyRound, ShieldCheck, Sliders, Sparkles } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@marine/ui/tabs';
import { KeysTab } from './KeysTab';
import { DefaultsTab } from './DefaultsTab';
import { PromptsTab } from './PromptsTab';
import { UsageTab } from './UsageTab';

/**
 * Platform → AI Settings (Phase 39.2, Prompts tab added #867).
 *
 * Four tabs:
 *   - Keys     (Phase 39)   — singleton `platform_ai_settings` row (4 provider keys)
 *   - Defaults (Phase 39.2) — normalized `platform_ai_feature_configs` rows
 *                             (6 features × {model, temperature, max_tokens})
 *   - Prompts  (#867)       — landlord `platform_ai_prompts` DEFAULT template
 *                             editor (system prompt / user prompt template /
 *                             per-template overrides), the GUI-managed
 *                             fallback every dealership inherits until it
 *                             customizes its own tenant `ai_prompts` row.
 *   - Usage    (Phase 39.3) — browse-only cost aggregates
 *
 * Tab chrome mirrors DealershipSettingsPage (research §Q1 canonical pattern).
 * Page header is shared; the security notice stays inside the Keys tab body
 * since it's specific to secret handling.
 */
export function PlatformAiSettingsPage() {
  const [activeTab, setActiveTab] = useState<'keys' | 'defaults' | 'prompts' | 'usage'>('keys');

  return (
    <div className="space-y-6 p-5 pb-8">
      {/* Page header (shared across tabs) */}
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Sparkles className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-lg font-semibold">AI Settings</h1>
          <p className="text-xs text-muted-foreground">
            Provider API keys and per-feature model defaults used by the AI engine.
          </p>
        </div>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(v) => setActiveTab(v as 'keys' | 'defaults' | 'prompts' | 'usage')}
        className="text-sm"
      >
        <TabsList
          variant="line"
          className="px-5 gap-6 bg-transparent [&_button]:border-b [&_button_svg]:size-4 [&_button]:text-secondary-foreground"
        >
          <TabsTrigger value="keys">
            <KeyRound />
            Keys
          </TabsTrigger>
          <TabsTrigger value="defaults">
            <Sliders />
            Defaults
          </TabsTrigger>
          <TabsTrigger value="prompts">
            <FileText />
            Prompts
          </TabsTrigger>
          <TabsTrigger value="usage">
            <Activity />
            Usage
          </TabsTrigger>
        </TabsList>

        <TabsContent value="keys" className="space-y-6 pt-4">
          {/* Security notice — Keys-tab-specific (applies to raw key handling). */}
          <div className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/30">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-amber-700 dark:text-amber-400" />
            <div className="text-xs text-amber-900 dark:text-amber-200">
              Keys are encrypted at rest in the landlord database and travel to the AI engine
              inside HMAC-signed request bodies. Raw key material is never returned over the API
              after it's saved — display values show only the prefix and last 4 characters.
            </div>
          </div>
          <KeysTab />
        </TabsContent>

        <TabsContent value="defaults" className="space-y-6 pt-4">
          <DefaultsTab />
        </TabsContent>

        <TabsContent value="prompts" className="space-y-6 pt-4">
          <PromptsTab />
        </TabsContent>

        <TabsContent value="usage" className="space-y-6 pt-4">
          <UsageTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
